import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface NotificationCounts {
  contactLeads: number;
  phoneLeads: number;
  smsMessages: number;
}

export const useNotificationCounts = () => {
  const [counts, setCounts] = useState<NotificationCounts>({
    contactLeads: 0,
    phoneLeads: 0,
    smsMessages: 0,
  });

  const fetchCounts = async () => {
    try {
      // Fetch new contact leads (status = 'new')
      const { count: contactCount } = await supabase
        .from('contact_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'new');

      // Fetch new phone leads (status = 'new')
      const { count: phoneCount } = await supabase
        .from('phone_leads')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'new');

      // Fetch unread SMS messages (unread_count > 0 and status = 'active')
      const { count: smsCount } = await supabase
        .from('sms_conversations')
        .select('*', { count: 'exact', head: true })
        .gt('unread_count', 0)
        .eq('status', 'active');

      setCounts({
        contactLeads: contactCount || 0,
        phoneLeads: phoneCount || 0,
        smsMessages: smsCount || 0,
      });
    } catch (error) {
      console.error('Error fetching notification counts:', error);
    }
  };

  useEffect(() => {
    fetchCounts();

    // Set up real-time subscriptions
    const contactChannel = supabase
      .channel('contact-submissions-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contact_submissions',
        },
        () => {
          fetchCounts();
        }
      )
      .subscribe();

    const phoneChannel = supabase
      .channel('phone-leads-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'phone_leads',
        },
        () => {
          fetchCounts();
        }
      )
      .subscribe();

    const smsChannel = supabase
      .channel('sms-conversations-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sms_conversations',
        },
        () => {
          fetchCounts();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(contactChannel);
      supabase.removeChannel(phoneChannel);
      supabase.removeChannel(smsChannel);
    };
  }, []);

  return counts;
};
